import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import type { Notification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
}

/** 個別通知アイテム */
export function NotificationItem({
  notification,
  onMarkRead,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const isUnread = notification.read_at === null;

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: ja,
  });

  const handleClick = () => {
    if (isUnread && onMarkRead) {
      onMarkRead(notification.id);
    }
    if (notification.link_url) {
      // 内部リンクはreact-router、外部URLは新タブで開く
      if (notification.link_url.startsWith("http")) {
        window.open(notification.link_url, "_blank", "noopener,noreferrer");
      } else {
        navigate(notification.link_url);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "w-full text-left px-4 py-3 border-b last:border-b-0",
        "hover:bg-muted/50 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        notification.link_url ? "cursor-pointer" : "cursor-default"
      )}
      aria-label={`${notification.title}${isUnread ? "（未読）" : ""}`}
    >
      <div className="flex items-start gap-2">
        {/* 未読インジケーター */}
        <span
          className={cn(
            "mt-1.5 h-2 w-2 shrink-0 rounded-full",
            isUnread ? "bg-destructive" : "bg-transparent"
          )}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm leading-snug",
              isUnread ? "font-medium" : "font-normal text-muted-foreground"
            )}
          >
            {notification.title}
          </p>
          {notification.body && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {notification.body}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        </div>
      </div>
    </button>
  );
}
